// src/features/categories/pages/CategoryDetailPage.tsx
// ENHANCED CATEGORY DETAIL PAGE

import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import {
    getCategory, updateCategory, deleteCategory, reassignProducts,
    CategoryData, DeleteCategoryResult
} from './services/categoriesApi';
import { getProducts, ProductData, toggleClearance, deleteProduct } from '@/features/products/services/productsApi';
import {
    ArrowLeft, Pencil, FolderTree, Package, Layers,
    Boxes, Clock, Search, X, Plus,
    LayoutGrid, List, RefreshCw, Trash2, Tag,
    Pill, Ruler, ChevronLeft, ChevronRight,
    ChevronsLeft, ChevronsRight, CheckCircle,
    Save,
    AlertTriangle, MoveRight, AlertCircle,

    PartyPopper
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import apiClient from '@/lib/api-client';
import React from 'react';

// ============================================================
// CONSTANTS
// ============================================================

const PAGE_SIZE = 25;

// ============================================================
// HELPERS
// ============================================================

function extractErrorMessage(err: any): string {
    const apiError = err?.response?.data?.error;
    if (!apiError) return err?.message || 'An unexpected error occurred.';
    if (apiError.details && Array.isArray(apiError.details) && apiError.details.length > 0) {
        return apiError.details.map((d: any) => d.message).join('. ');
    }
    return apiError.message || 'An unexpected error occurred.';
}

function extractFieldErrors(err: any): Record<string, string> {
    const apiError = err?.response?.data?.error;
    if (!apiError?.details || !Array.isArray(apiError.details)) return {};
    const fieldErrors: Record<string, string> = {};
    apiError.details.forEach((detail: any) => {
        if (detail.field && detail.message) fieldErrors[detail.field] = detail.message;
    });
    return fieldErrors;
}

function CelebrationOverlay({ show, message, onComplete }: {
    show: boolean; message: string; onComplete: () => void;
}) {
    useEffect(() => {
        if (show) { const timer = setTimeout(onComplete, 2500); return () => clearTimeout(timer); }
    }, [show, onComplete]);
    if (!show) return null;
    const colors = ['#10B981', '#06B6D4', '#8B5CF6', '#F59E0B', '#EC4899', '#3B82F6'];
    const pieces = Array.from({ length: 40 }, (_, i) => ({
        id: i, left: `${Math.random() * 100}%`, delay: Math.random() * 1.5,
        duration: 1.5 + Math.random() * 1.5, color: colors[Math.floor(Math.random() * colors.length)],
        size: 6 + Math.random() * 10
    }));
    return (
        <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-[60] pointer-events-none overflow-hidden">
                {pieces.map(p => (
                    <motion.div key={p.id} initial={{ y: -20, opacity: 1 }}
                        animate={{ y: '100vh', x: `${(Math.random() - 0.5) * 150}px`, opacity: 0, rotate: 360 }}
                        transition={{ duration: p.duration, delay: p.delay, ease: 'easeIn' }}
                        className="absolute top-0 rounded-sm"
                        style={{ left: p.left, width: p.size, height: p.size * 0.6, backgroundColor: p.color }} />
                ))}
                <motion.div initial={{ scale: 0, y: -50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0, y: -50 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                    className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto">
                    <div className="bg-white rounded-3xl shadow-2xl p-6 text-center border-2 border-emerald-200">
                        <motion.div animate={{ rotate: [0, -10, 10, -10, 0] }}
                            transition={{ duration: 0.8, repeat: Infinity, repeatDelay: 1 }}
                            className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mx-auto mb-3 shadow-lg shadow-emerald-200">
                            <PartyPopper size={32} className="text-white" />
                        </motion.div>
                        <h3 className="text-lg font-black text-slate-900">{message}</h3>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

// ============================================================
// STAT CARD
// ============================================================

function StatCard({ label, value, icon: Icon, gradient }: {
    label: string;
    value: number | string;
    icon: React.ElementType;
    gradient: string;
}) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -3 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-lg transition-all duration-300"
        >
            <div className="flex items-start justify-between">
                <div className="space-y-1">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">
                        {typeof value === 'number' ? value.toLocaleString() : value}
                    </p>
                </div>
                <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient} shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                </div>
            </div>
        </motion.div>
    );
}

// ============================================================
// STATUS BADGE
// ============================================================

function StatusBadge({ status }: { status: 'active' | 'inactive' | 'clearance' }) {
    const variants: Record<string, string> = {
        active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        inactive: 'bg-rose-50 text-rose-700 border-rose-200',
        clearance: 'bg-amber-50 text-amber-700 border-amber-200',
    };
    const dots: Record<string, string> = {
        active: 'bg-emerald-500',
        inactive: 'bg-rose-500',
        clearance: 'bg-amber-500',
    };

    return (
        <span className={clsx('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border', variants[status])}>
            <span className={clsx('w-1.5 h-1.5 rounded-full', dots[status])} />
            {status === 'active' ? 'Active' : status === 'inactive' ? 'Inactive' : 'Clearance'}
        </span>
    );
}

// ============================================================
// PAGINATION
// ============================================================

function Pagination({ currentPage, totalPages, totalItems, pageSize, onPageChange, isLoading }: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    isLoading: boolean;
}) {
    if (totalItems === 0) return null;

    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    const pageNumbers: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
        pageNumbers.push(1);
        if (currentPage > 3) pageNumbers.push('ellipsis');
        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);
        for (let i = start; i <= end; i++) pageNumbers.push(i);
        if (currentPage < totalPages - 2) pageNumbers.push('ellipsis');
        pageNumbers.push(totalPages);
    }

    if (totalPages <= 1 && !isLoading) return null;

    return (
        <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-600">
                Showing <span className="font-semibold text-slate-900">{startItem}</span> to{' '}
                <span className="font-semibold text-slate-900">{endItem}</span> of{' '}
                <span className="font-semibold text-slate-900">{totalItems.toLocaleString()}</span> products
            </p>
            <div className="flex gap-2 flex-wrap">
                <button onClick={() => onPageChange(1)} disabled={currentPage === 1 || isLoading} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 disabled:opacity-30" type="button"><ChevronsLeft className="w-4 h-4" /></button>
                <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading} className="px-4 py-2 border border-slate-200 rounded-xl text-sm disabled:opacity-40 hover:bg-slate-100 font-medium transition" type="button"><ChevronLeft className="w-4 h-4" /></button>
                {pageNumbers.map((page, idx) => (
                    <React.Fragment key={idx}>
                        {page === 'ellipsis' ? (
                            <span className="px-2 py-2 text-slate-400">...</span>
                        ) : (
                            <button
                                onClick={() => onPageChange(page as number)}
                                disabled={isLoading}
                                className={clsx(
                                    'px-4 py-2 rounded-xl text-sm font-medium transition',
                                    currentPage === page
                                        ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-md shadow-emerald-500/25'
                                        : 'border border-slate-200 hover:bg-slate-100 text-slate-700'
                                )}
                                type="button">
                                {page}
                            </button>
                        )}
                    </React.Fragment>
                ))}
                <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages || isLoading} className="px-4 py-2 border border-slate-200 rounded-xl text-sm disabled:opacity-40 hover:bg-slate-100 font-medium transition" type="button"><ChevronRight className="w-4 h-4" /></button>
                <button onClick={() => onPageChange(totalPages)} disabled={currentPage >= totalPages || isLoading} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 disabled:opacity-30" type="button"><ChevronsRight className="w-4 h-4" /></button>
            </div>
        </div>
    );
}

// ============================================================
// PRODUCT CARD
// ============================================================

function ProductCard({ product, navigate }: { product: ProductData; navigate: (path: string) => void }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -5 }}
            onClick={() => navigate(`/products/${product.id}`)}
            className="group bg-white rounded-2xl border-2 border-slate-200 shadow-sm overflow-hidden transition-all duration-300 cursor-pointer hover:shadow-xl hover:border-emerald-200"
        >
            <div className="h-1.5 bg-gradient-to-r from-emerald-500 to-teal-600" />
            <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                    <div className="flex gap-1 flex-wrap">
                        {product.isClearance && <StatusBadge status="clearance" />}
                        {product.isPharmaceutical && <Badge variant="info" size="sm"><Pill className="w-3 h-3 mr-0.5" />Rx</Badge>}
                        {!product.isActive && <StatusBadge status="inactive" />}
                    </div>
                </div>
                <div className="aspect-square rounded-xl bg-slate-100 flex items-center justify-center mb-3 overflow-hidden">
                    {product.imageUrls?.[0] ? (
                        <img src={product.imageUrls[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    ) : (
                        <div className="text-center">
                            <Package className="w-10 h-10 text-slate-300 mx-auto" />
                            <span className="text-2xl font-bold text-slate-300 mt-1 block">{product.name.charAt(0).toUpperCase()}</span>
                        </div>
                    )}
                </div>
                <h3 className="text-sm font-semibold text-slate-900 truncate">{product.name}</h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {product.brand && <span className="text-[11px] text-slate-500 font-medium">{product.brand}</span>}
                    {product.sku && <code className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded font-mono">{product.sku}</code>}
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                    <p className="text-lg font-bold text-emerald-600">₦{parseFloat(product.retailPrice || '0').toLocaleString()}</p>
                    <div className="flex items-center gap-1">
                        {product.hasVariants && <Badge variant="info" size="sm"><Boxes className="w-3 h-3 mr-0.5" />{product.variants?.length || 0}</Badge>}
                        {(product.uoms?.length ?? 0) > 1 && <Badge variant="neutral" size="sm"><Ruler className="w-3 h-3 mr-0.5" />{product.uoms?.length}</Badge>}
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

// ============================================================
// SKELETONS
// ============================================================

function TableSkeleton({ rows = 8 }: { rows?: number }) {
    return (
        <div className="divide-y divide-slate-100">
            {Array.from({ length: rows }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-6 py-4 animate-pulse" style={{ animationDelay: `${i * 50}ms` }}>
                    <div className="w-5 h-5 bg-slate-200 rounded" />
                    <div className="w-10 h-10 bg-slate-200 rounded-xl flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                        <div className="h-4 bg-slate-200 rounded w-1/3" />
                        <div className="h-3 bg-slate-200 rounded w-1/4" />
                    </div>
                    <div className="w-20 h-4 bg-slate-200 rounded" />
                    <div className="w-24 h-4 bg-slate-200 rounded" />
                    <div className="w-16 h-6 bg-slate-200 rounded-full" />
                    <div className="w-24 h-4 bg-slate-200 rounded" />
                </div>
            ))}
        </div>
    );
}

function CardSkeleton({ count = 8 }: { count?: number }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {Array.from({ length: count }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-200 overflow-hidden animate-pulse" style={{ animationDelay: `${i * 50}ms` }}>
                    <div className="h-1.5 bg-slate-200" />
                    <div className="p-5 space-y-3">
                        <div className="flex justify-between">
                            <div className="w-5 h-5 bg-slate-200 rounded" />
                            <div className="flex gap-1">
                                <div className="w-14 h-5 bg-slate-200 rounded-full" />
                                <div className="w-14 h-5 bg-slate-200 rounded-full" />
                            </div>
                        </div>
                        <div className="aspect-square bg-slate-200 rounded-xl" />
                        <div className="h-4 bg-slate-200 rounded w-3/4" />
                        <div className="h-3 bg-slate-200 rounded w-1/2" />
                        <div className="flex justify-between pt-3 border-t border-slate-100">
                            <div className="h-5 bg-slate-200 rounded w-20" />
                            <div className="h-5 bg-slate-200 rounded w-12" />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function CategoryDetailPage() {
    const { categoryId } = useParams<{ categoryId: string }>();
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [viewMode, setViewMode] = useState<'table' | 'card'>('table');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
    const [showEditForm, setShowEditForm] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState<ProductData | null>(null);
    const [showCategoryDelete, setShowCategoryDelete] = useState(false);
    const [showReassignModal, setShowReassignModal] = useState(false);
    const [reassignTargetId, setReassignTargetId] = useState('');
    const [deleteResult, setDeleteResult] = useState<DeleteCategoryResult | null>(null);

    const [editName, setEditName] = useState('');
    const [editSlug, setEditSlug] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editParentId, setEditParentId] = useState('');
    const [editIsActive, setEditIsActive] = useState(true);
    const [slugError, setSlugError] = useState<string | null>(null);

    const [showCelebration, setShowCelebration] = useState(false);
    const [celebrationMsg, setCelebrationMsg] = useState('');

    // ============================================================
    // QUERIES
    // ============================================================

    const { data: category, isLoading: catLoading, isError: catError, refetch: refetchCategory } = useQuery<CategoryData>({
        queryKey: ['category', categoryId],
        queryFn: () => getCategory(user!.tenantId, categoryId!),
        enabled: !!user?.tenantId && !!categoryId,
    });

    const {
        data: productsResponse,
        isLoading: productsLoading,
        isFetching: productsFetching,
        refetch: refetchProducts,
    } = useQuery({
        queryKey: ['category-products', user?.tenantId, categoryId, search, page],
        queryFn: () =>
            getProducts(user!.tenantId, {
                categoryId,
                search: search || undefined,
                limit: PAGE_SIZE,
                cursor: page > 1 ? String((page - 1) * PAGE_SIZE) : undefined,
                sortBy: 'name',
                order: 'asc',
            }),
        enabled: !!user?.tenantId && !!categoryId,
        placeholderData: (prev: any) => prev,
    });

    const { data: categoriesFlat } = useQuery<CategoryData[]>({
        queryKey: ['categories-flat', user?.tenantId],
        queryFn: async () => {
            const { data } = await apiClient.get(`/tenants/${user!.tenantId}/categories`);
            return data.data;
        },
        enabled: !!user?.tenantId,
    });

    useEffect(() => {
        if (category) {
            setEditName(category.name);
            setEditSlug(category.slug);
            setEditDescription(category.description || '');
            setEditParentId(category.parentId || '');
            setEditIsActive(category.isActive);
        }
    }, [category]);

    useEffect(() => {
        if (editSlug) {
            if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(editSlug)) {
                setSlugError('Slug can only contain lowercase letters, numbers, and hyphens');
            } else if (editSlug.startsWith('-') || editSlug.endsWith('-')) {
                setSlugError('Slug cannot start or end with hyphen');
            } else {
                setSlugError(null);
            }
        } else {
            setSlugError('Slug is required');
        }
    }, [editSlug]);

    // ============================================================
    // MUTATIONS
    // ============================================================

    const updateMutation = useMutation({
        mutationFn: (data: any) => updateCategory(user!.tenantId, categoryId!, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['category', categoryId] });
            queryClient.invalidateQueries({ queryKey: ['categories-tree'] });
            queryClient.invalidateQueries({ queryKey: ['categories-flat'] });
            setShowEditForm(false);
            setCelebrationMsg('Category Updated! ✨');
            setShowCelebration(true);
            addToast({ type: 'success', title: 'Category Updated ✨', message: 'Category details saved.' });
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Update Failed', message: extractErrorMessage(err) }),
    });

    const deleteCategoryMutation = useMutation({
        mutationFn: () => deleteCategory(user!.tenantId, categoryId!),
        onSuccess: (result: DeleteCategoryResult) => {
            addToast({ type: 'success', title: 'Category Deleted', message: 'Category has been removed.' });
            queryClient.invalidateQueries({ queryKey: ['categories-tree'] });
            queryClient.invalidateQueries({ queryKey: ['categories-flat'] });
            setShowCategoryDelete(false);
            navigate('/categories');
        },
        onError: (err: any) => {
            const errorData = err?.response?.data?.error;
            if (errorData?.code === 'CATEGORY_HAS_PRODUCTS') {
                setDeleteResult({ productCount: totalCount, childCount: subCategories.length, blocked: true, message: errorData?.message });
                setShowCategoryDelete(false);
                setShowReassignModal(true);
            } else {
                addToast({ type: 'error', title: 'Delete Failed', message: extractErrorMessage(err) });
                setShowCategoryDelete(false);
            }
        },
    });

    const reassignAndDeleteMutation = useMutation({
        mutationFn: async (targetId: string) => { await reassignProducts(user!.tenantId, categoryId!, targetId); await deleteCategory(user!.tenantId, categoryId!); },
        onSuccess: () => {
            addToast({ type: 'success', title: 'Products Reassigned & Deleted', message: 'Category deleted.' });
            setShowReassignModal(false);
            queryClient.invalidateQueries({ queryKey: ['categories-tree'] });
            queryClient.invalidateQueries({ queryKey: ['categories-flat'] });
            navigate('/categories');
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Operation Failed', message: extractErrorMessage(err) }),
    });

    const toggleClearanceMutation = useMutation({
        mutationFn: (productId: string) => toggleClearance(user!.tenantId, productId),
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['category-products'] }); addToast({ type: 'success', title: 'Clearance Toggled', message: 'Product clearance status updated.' }); },
        onError: (err: any) => addToast({ type: 'error', title: 'Toggle Failed', message: extractErrorMessage(err) }),
    });

    const deleteProductMutation = useMutation({
        mutationFn: (productId: string) => deleteProduct(user!.tenantId, productId),
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['category-products'] }); setDeleteConfirm(null); addToast({ type: 'success', title: 'Product Deleted', message: 'Product has been removed.' }); },
        onError: (err: any) => addToast({ type: 'error', title: 'Delete Failed', message: extractErrorMessage(err) }),
    });

    // ============================================================
    // DERIVED DATA
    // ============================================================

    const products: ProductData[] = productsResponse?.data || [];
    const totalCount: number = productsResponse?.meta?.totalCount || 0;
    const totalPages: number = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const subCategories = (categoriesFlat || []).filter((c) => c.parentId === categoryId);
    const categoryColor = category?.color || '#0D9488';

    // ============================================================
    // HANDLERS
    // ============================================================

    const handlePageChange = useCallback((newPage: number) => {
        setPage(newPage);
        setSelectedProducts(new Set());
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const toggleSelectAll = () => {
        if (selectedProducts.size === products.length && products.length > 0) {
            setSelectedProducts(new Set());
        } else {
            setSelectedProducts(new Set(products.map((p) => p.id)));
        }
    };

    const toggleSelect = (id: string) => {
        setSelectedProducts((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleSaveEdit = (e: React.FormEvent) => {
        e.preventDefault();
        if (slugError) return;
        updateMutation.mutate({
            name: editName,
            slug: editSlug,
            description: editDescription || null,
            parentId: editParentId || null,
            isActive: editIsActive,
        });
    };

    const handleReassignAndNavigate = () => {
        if (reassignTargetId) {
            reassignAndDeleteMutation.mutate(reassignTargetId);
        } else {
            addToast({ type: 'warning', title: 'Please select a target category' });
        }
    };

    // ============================================================
    // LOADING
    // ============================================================

    if (catLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Spinner size="xl" />
            </div>
        );
    }

    if (catError || !category) {
        return (
            <ErrorState
                title="Category not found"
                message="This category doesn't exist or has been deleted."
                onRetry={() => navigate('/categories')}
            />
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            <CelebrationOverlay show={showCelebration} message={celebrationMsg} onComplete={() => setShowCelebration(false)} />
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={() => navigate('/categories')} className="p-2 rounded-xl hover:bg-slate-100 transition-colors" type="button">
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 rounded-2xl shadow-lg flex-shrink-0" style={{ background: `linear-gradient(135deg, ${categoryColor}, ${categoryColor}cc)` }}>
                                <FolderTree className="w-5 h-5 text-white" />
                            </div>
                            <div className="min-w-0">
                                <h1 className="text-2xl font-bold text-slate-900 truncate">{category.name}</h1>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <code className="text-xs text-slate-400 font-mono">/{category.slug}</code>
                                    <StatusBadge status={category.isActive ? 'active' : 'inactive'} />
                                    {category.description && (
                                        <span className="text-xs text-slate-400 hidden sm:inline">· {category.description}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <button onClick={() => setShowEditForm(true)} className="flex items-center gap-1.5 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm" type="button">
                                <Pencil className="w-4 h-4" /> Edit
                            </button>
                            <button onClick={() => setShowCategoryDelete(true)} className="flex items-center gap-1.5 px-4 py-2.5 border border-rose-200 text-rose-600 rounded-xl hover:bg-rose-50 transition-colors text-sm font-medium" type="button">
                                <Trash2 className="w-4 h-4" /> Delete
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-6">
                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard label="Products" value={totalCount} icon={Package} gradient="from-emerald-500 to-teal-600" />
                    <StatCard label="Sub-Categories" value={subCategories.length} icon={Layers} gradient="from-blue-500 to-indigo-600" />
                    <StatCard label="Active" value={products.filter((p) => p.isActive).length} icon={CheckCircle} gradient="from-emerald-500 to-teal-600" />
                    <StatCard label="Status" value={category.isActive ? 'Active' : 'Inactive'} icon={Clock} gradient={category.isActive ? 'from-emerald-500 to-teal-600' : 'from-rose-400 to-rose-600'} />
                </div>

                {/* Sub-Categories */}
                {subCategories.length > 0 && (
                    <Card>
                        <CardHeader gradient>
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 ring-1 ring-emerald-200/50">
                                    <Layers className="w-5 h-5 text-emerald-600" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-slate-800">Sub-Categories</h3>
                                    <p className="text-xs text-slate-500">{subCategories.length} sub-categories</p>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                {subCategories.map((sub) => (
                                    <button
                                        key={sub.id}
                                        onClick={() => navigate(`/categories/${sub.id}`)}
                                        className="p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all text-left group"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold shadow-sm" style={{ backgroundColor: sub.color || '#0D9488' }}>
                                                {sub.name.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-semibold text-sm truncate group-hover:text-emerald-600 transition-colors">{sub.name}</p>
                                                <p className="text-xs text-slate-400">{sub.productCount || 0} products</p>
                                            </div>
                                            <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-500 ml-auto flex-shrink-0 transition-colors" />
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Products Section */}
                <Card>
                    <CardHeader gradient>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 ring-1 ring-emerald-200/50">
                                    <Package className="w-5 h-5 text-emerald-600" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-slate-800">Products</h3>
                                    <p className="text-xs text-slate-500">{totalCount.toLocaleString()} products in this category</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder="Search products..."
                                        value={search}
                                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                                        className="pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 w-48"
                                    />
                                    {search && (
                                        <button onClick={() => { setSearch(''); setPage(1); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" type="button">
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                                <button onClick={() => refetchProducts()} disabled={productsFetching} className="p-2 rounded-lg text-slate-400 hover:text-slate-600" type="button">
                                    <RefreshCw className={clsx('w-4 h-4', productsFetching && 'animate-spin')} />
                                </button>
                                <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
                                    <button onClick={() => setViewMode('table')} className={clsx('p-1.5 rounded-md', viewMode === 'table' ? 'bg-white shadow-sm' : 'text-slate-400')} type="button"><List className="w-4 h-4" /></button>
                                    <button onClick={() => setViewMode('card')} className={clsx('p-1.5 rounded-md', viewMode === 'card' ? 'bg-white shadow-sm' : 'text-slate-400')} type="button"><LayoutGrid className="w-4 h-4" /></button>
                                </div>
                                <button
                                    onClick={() => navigate('/products/new')}
                                    className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all font-semibold text-sm shadow-lg shadow-emerald-500/25"
                                    type="button"
                                >
                                    <Plus className="w-4 h-4" /> Add
                                </button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        {productsLoading && !productsResponse ? (
                            viewMode === 'table' ? <TableSkeleton rows={PAGE_SIZE} /> : <CardSkeleton count={PAGE_SIZE} />
                        ) : products.length === 0 ? (
                            search ? (
                                <div className="text-center py-16">
                                    <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                                        <Search className="w-10 h-10 text-slate-300" />
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-600">No products match "{search}"</h3>
                                    <p className="text-sm text-slate-400 mt-1">Try adjusting your search terms.</p>
                                    <button onClick={() => setSearch('')} className="mt-4 px-5 py-2.5 border-2 border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50" type="button">Clear Search</button>
                                </div>
                            ) : (
                                <div className="text-center py-16">
                                    <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                                        <Package className="w-10 h-10 text-slate-300" />
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-600">No products in this category</h3>
                                    <p className="text-sm text-slate-400 mt-1">Add products to start building this category.</p>
                                    <button onClick={() => navigate('/products/new')} className="mt-4 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 transition-all font-semibold text-sm shadow-lg shadow-emerald-500/25 inline-flex items-center gap-2" type="button"><Plus className="w-4 h-4" />Add Product</button>
                                </div>
                            )
                        ) : viewMode === 'table' ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead>
                                        <tr className="bg-slate-50/50">
                                            <th className="px-6 py-4 text-left">
                                                <button onClick={toggleSelectAll} className="text-slate-300 hover:text-emerald-500 transition-colors" type="button">
                                                    {selectedProducts.size === products.length && products.length > 0 ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <div className="w-4 h-4 rounded border-2 border-slate-300" />}
                                                </button>
                                            </th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">SKU/Barcode</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Price</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {products.map((product, idx) => (
                                            <motion.tr
                                                key={product.id}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ delay: Math.min(idx * 0.02, 0.3) }}
                                                className={clsx('hover:bg-emerald-50/20 transition-colors cursor-pointer group', selectedProducts.has(product.id) && 'bg-emerald-50/50')}
                                                onClick={() => navigate(`/products/${product.id}`)}
                                            >
                                                <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                                                    <button onClick={() => toggleSelect(product.id)} type="button">
                                                        {selectedProducts.has(product.id) ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <div className="w-4 h-4 rounded border-2 border-slate-300 group-hover:border-emerald-400 transition-colors" />}
                                                    </button>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                                                            {product.imageUrls?.[0] ? <img src={product.imageUrls[0]} alt="" className="w-full h-full object-cover" /> : <Package className="w-5 h-5 text-slate-400" />}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-medium text-slate-900 truncate max-w-[200px]">{product.name}</p>
                                                            <p className="text-xs text-slate-400 truncate">{product.brand || '—'}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 hidden md:table-cell">
                                                    <div className="text-xs space-y-0.5">
                                                        {product.sku && <p className="font-mono text-slate-500">SKU: {product.sku}</p>}
                                                        {product.barcode && <p className="font-mono text-slate-400 text-[10px]">{product.barcode}</p>}
                                                        {!product.sku && !product.barcode && <span className="text-slate-400">—</span>}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <p className="text-sm font-semibold text-slate-900 tabular-nums">₦{parseFloat(product.retailPrice || '0').toLocaleString()}</p>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    {!product.isActive ? <StatusBadge status="inactive" /> : product.isClearance ? <StatusBadge status="clearance" /> : <StatusBadge status="active" />}
                                                </td>
                                                <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                                                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button onClick={() => navigate(`/products/${product.id}/edit`)} className="p-1.5 rounded-lg hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 transition-colors" type="button" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                                                        <button onClick={() => toggleClearanceMutation.mutate(product.id)} className="p-1.5 rounded-lg hover:bg-amber-50 text-slate-400 hover:text-amber-600 transition-colors" type="button" title="Toggle Clearance"><Tag className="w-3.5 h-3.5" /></button>
                                                        <button onClick={() => setDeleteConfirm(product)} className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors" type="button" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                                                    </div>
                                                </td>
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="p-6">
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                                    {products.map((product) => (
                                        <ProductCard key={product.id} product={product} navigate={navigate} />
                                    ))}
                                </div>
                            </div>
                        )}
                        <Pagination currentPage={page} totalPages={totalPages} totalItems={totalCount} pageSize={PAGE_SIZE} onPageChange={handlePageChange} isLoading={productsFetching} />
                    </CardContent>
                </Card>
            </div>

            {/* Edit Category Modal */}
            <AnimatePresence>
                {showEditForm && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 overflow-y-auto">
                        <div className="flex items-center justify-center min-h-screen px-4 py-8">
                            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowEditForm(false)} />
                            <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }} transition={{ type: "spring", stiffness: 300, damping: 30 }} className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
                                <div className="flex-shrink-0 px-8 py-5 bg-gradient-to-r from-emerald-600 to-teal-600">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3"><div className="p-1.5 rounded-lg bg-white/20"><Pencil className="w-4 h-4 text-white" /></div><div><h2 className="text-lg font-bold text-white">Edit Category</h2><p className="text-emerald-100 text-xs">Update category details</p></div></div>
                                        <button onClick={() => setShowEditForm(false)} className="p-1.5 rounded-lg bg-white/20 text-white hover:bg-white/30" type="button"><X className="w-4 h-4" /></button>
                                    </div>
                                </div>
                                <div className="flex-1 overflow-y-auto p-8">
                                    <form onSubmit={handleSaveEdit} className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Name <span className="text-rose-500">*</span></label>
                                            <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400" required />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Slug <span className="text-rose-500">*</span></label>
                                            <input type="text" value={editSlug} onChange={(e) => setEditSlug(e.target.value)} className={clsx('w-full px-4 py-3 border-2 rounded-xl text-sm font-mono focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400', slugError ? 'border-rose-300 bg-rose-50' : 'border-slate-200')} required />
                                            {slugError && <p className="text-xs text-rose-500 mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{slugError}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Description</label>
                                            <textarea value={editDescription} onChange={(e) => setEditDescription(e.target.value)} rows={2} className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm resize-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Parent Category</label>
                                            <select value={editParentId} onChange={(e) => setEditParentId(e.target.value)} className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400">
                                                <option value="">None (Top Level)</option>
                                                {(categoriesFlat || []).filter((c) => c.id !== categoryId).map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                                            </select>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="checkbox" checked={editIsActive} onChange={(e) => setEditIsActive(e.target.checked)} className="rounded border-slate-300 text-emerald-600" />
                                                <span className="text-sm font-medium text-slate-700">Active</span>
                                            </label>
                                        </div>
                                    </form>
                                </div>
                                <div className="flex-shrink-0 px-8 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3 rounded-b-3xl">
                                    <button onClick={() => setShowEditForm(false)} className="px-5 py-2.5 border-2 border-slate-300 rounded-xl hover:bg-white transition font-medium text-sm" type="button">Cancel</button>
                                    <button onClick={handleSaveEdit} disabled={updateMutation.isPending || !!slugError} className="px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 transition-all font-semibold text-sm shadow-lg shadow-emerald-500/25 flex items-center gap-1.5">{updateMutation.isPending ? <Spinner size="xs" variant="white" /> : <Save className="w-4 h-4" />}Save Changes</button>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Category Delete Modal */}
            <AnimatePresence>
                {showCategoryDelete && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowCategoryDelete(false)} />
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md p-8">
                            <div className="text-center">
                                <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-4"><Trash2 className="w-8 h-8 text-rose-500" /></div>
                                <h2 className="text-xl font-bold text-slate-900">Delete "{category.name}"?</h2>
                                {totalCount > 0 ? (
                                    <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-left">
                                        <div className="flex items-start gap-2"><AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-amber-800">This category contains {totalCount} product{totalCount !== 1 ? 's' : ''}</p><p className="text-xs text-amber-600 mt-1">You can reassign these products to another category before deleting.</p></div></div>
                                    </div>
                                ) : (
                                    <p className="text-sm text-slate-500 mt-2">This category is empty and can be safely deleted.</p>
                                )}
                                {subCategories.length > 0 && (
                                    <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-xl text-left"><div className="flex items-start gap-2"><FolderTree className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" /><div><p className="text-xs text-slate-600"><strong>{subCategories.length} sub-categor{subCategories.length === 1 ? 'y' : 'ies'}</strong> will be moved to parent level</p></div></div></div>
                                )}
                            </div>
                            <div className="flex gap-3 mt-8">
                                <button onClick={() => setShowCategoryDelete(false)} className="flex-1 px-5 py-3 border-2 border-slate-300 rounded-xl hover:bg-white transition font-medium text-sm" type="button">Cancel</button>
                                <button onClick={() => deleteCategoryMutation.mutate()} className={clsx('flex-1 px-5 py-3 text-white rounded-xl transition-all font-semibold text-sm shadow-lg', totalCount > 0 ? 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 shadow-blue-500/25' : 'bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 shadow-rose-500/25')} type="button" disabled={deleteCategoryMutation.isPending}>
                                    {deleteCategoryMutation.isPending ? <Spinner size="xs" variant="white" /> : totalCount > 0 ? 'Delete & Reassign' : 'Delete Category'}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Reassign Modal */}
            <AnimatePresence>
                {showReassignModal && deleteResult && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowReassignModal(false)} />
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md p-8">
                            <div className="text-center"><div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-4"><MoveRight className="w-8 h-8 text-blue-500" /></div><h2 className="text-xl font-bold text-slate-900">{deleteResult.blocked ? 'Cannot Delete Category' : 'Category Deleted'}</h2><p className="text-sm text-slate-500 mt-2">{deleteResult.blocked ? `"${category.name}" has ${deleteResult.productCount} product${deleteResult.productCount !== 1 ? 's' : ''}. Reassign them first.` : `${deleteResult.productCount} product${deleteResult.productCount !== 1 ? 's' : ''} from "${category.name}" need a new category`}</p></div>
                            <div className="mt-6">
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Reassign to Category</label>
                                <select value={reassignTargetId} onChange={(e) => setReassignTargetId(e.target.value)} className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400">
                                    <option value="">Select a category...</option>
                                    {(categoriesFlat || []).filter(c => c.id !== categoryId).map((c) => (<option key={c.id} value={c.id}>{c.name} {c.productCount !== undefined ? `(${c.productCount} products)` : ''}</option>))}
                                </select>
                            </div>
                            <div className="flex gap-3 mt-8">
                                <button onClick={() => { setShowReassignModal(false); navigate('/categories'); }} className="flex-1 px-5 py-3 border-2 border-slate-300 rounded-xl hover:bg-white transition font-medium text-sm text-slate-600" type="button">{deleteResult?.blocked ? 'Cancel' : 'Skip'}</button>
                                <button onClick={handleReassignAndNavigate} disabled={!reassignTargetId || reassignAndDeleteMutation.isPending} className="flex-1 px-5 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 transition-all font-semibold text-sm shadow-lg shadow-blue-500/25 disabled:opacity-50 flex items-center justify-center gap-2" type="button">{reassignAndDeleteMutation.isPending ? <Spinner size="xs" variant="white" /> : <MoveRight className="w-4 h-4" />}Reassign Products</button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Delete Product Modal */}
            <AnimatePresence>
                {deleteConfirm && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteConfirm(null)} />
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md p-8">
                            <div className="text-center"><div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-4"><Trash2 className="w-8 h-8 text-rose-500" /></div><h2 className="text-xl font-bold text-slate-900">Delete Product?</h2><p className="text-sm text-slate-500 mt-2">Are you sure you want to delete <strong>"{deleteConfirm.name}"</strong>?</p></div>
                            <div className="flex gap-3 mt-8"><button onClick={() => setDeleteConfirm(null)} className="flex-1 px-5 py-3 border-2 border-slate-300 rounded-xl hover:bg-white transition font-medium text-sm" type="button">Cancel</button><button onClick={() => deleteProductMutation.mutate(deleteConfirm.id)} className="flex-1 px-5 py-3 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-xl hover:from-rose-600 transition-all font-semibold text-sm shadow-lg shadow-rose-500/25" type="button">{deleteProductMutation.isPending ? 'Deleting...' : 'Delete'}</button></div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}